import React, { useEffect, useRef, useState } from "react";
import { Spreadsheet, SpreadsheetHandle } from "@progress/kendo-react-spreadsheet";
import { Button } from "@progress/kendo-react-buttons";
import LoadingBackdrop from "./common/LoadingBackdrop";
import { AppBar, Box, Toolbar } from "@material-ui/core";
import { Workbook } from "@progress/kendo-ooxml";
import JSZip from "jszip";

interface SpreadsheetViewerComponentProps {
    document: string; // Blob URL
    save?: boolean;
    readonly?: boolean;
    item?: any;
    woid?: number;
    filename?: string;
    saveDocumentCallback: (data: any) => void;
}

export default function SpreadsheetViewerComponent(props: SpreadsheetViewerComponentProps) {
    const spreadsheetRef = useRef<SpreadsheetHandle>(null);
    const [loadingBackdrop, setLoadingBackdrop] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    // Extract bitmap from EMF EMR_STRETCHDIBITS record
    const extractBitmapFromEmf = (emfArrayBuffer: ArrayBuffer): string | null => {
        try {
            const view = new DataView(emfArrayBuffer);

            // EMF header is 108 bytes, skip it and look for EMR_STRETCHDIBITS (0x51)
            let offset = 108;

            while (offset < emfArrayBuffer.byteLength - 8) {
                const recordType = view.getUint32(offset, true);
                const recordSize = view.getUint32(offset + 4, true);

                // EMR_STRETCHDIBITS = 0x51 (81 decimal)
                if (recordType === 0x51) {
                    console.log(`Found EMR_STRETCHDIBITS at offset ${offset}, size ${recordSize}`);

                    // Parse the record structure (according to MS-EMF spec)
                    // Offsets from record start (record includes Type+Size = 8 bytes)
                    const offBmiSrc = view.getUint32(offset + 48, true);
                    const cbBmiSrc = view.getUint32(offset + 52, true);
                    const offBitsSrc = view.getUint32(offset + 56, true);
                    const cbBitsSrc = view.getUint32(offset + 60, true);

                    console.log(`Bitmap offsets: bmi=${offBmiSrc}, bmiSize=${cbBmiSrc}, bits=${offBitsSrc}, bitsSize=${cbBitsSrc}`);

                    // Read the DIB header to get actual dimensions
                    const dibHeaderStart = offset + offBmiSrc;
                    const dibHeaderSize = view.getUint32(dibHeaderStart, true);
                    console.log(`DIB header size: ${dibHeaderSize}`);

                    // Extract bitmap info header and bits separately
                    const bmiStart = offset + offBmiSrc;
                    const bitsStart = offset + offBitsSrc;

                    // Create BMP file header (14 bytes)
                    const bmpFileHeader = new Uint8Array(14);
                    bmpFileHeader[0] = 0x42; // 'B'
                    bmpFileHeader[1] = 0x4D; // 'M'

                    const fileSize = 14 + cbBmiSrc + cbBitsSrc;
                    bmpFileHeader[2] = fileSize & 0xFF;
                    bmpFileHeader[3] = (fileSize >> 8) & 0xFF;
                    bmpFileHeader[4] = (fileSize >> 16) & 0xFF;
                    bmpFileHeader[5] = (fileSize >> 24) & 0xFF;

                    const pixelDataOffset = 14 + cbBmiSrc;
                    bmpFileHeader[10] = pixelDataOffset & 0xFF;
                    bmpFileHeader[11] = (pixelDataOffset >> 8) & 0xFF;
                    bmpFileHeader[12] = (pixelDataOffset >> 16) & 0xFF;
                    bmpFileHeader[13] = (pixelDataOffset >> 24) & 0xFF;

                    // Combine: BMP header + DIB header + pixel data
                    const bmpData = new Uint8Array(fileSize);
                    bmpData.set(bmpFileHeader, 0);
                    bmpData.set(new Uint8Array(emfArrayBuffer, bmiStart, cbBmiSrc), 14);
                    bmpData.set(new Uint8Array(emfArrayBuffer, bitsStart, cbBitsSrc), 14 + cbBmiSrc);

                    // Convert to base64
                    let binary = '';
                    for (let i = 0; i < bmpData.length; i++) {
                        binary += String.fromCharCode(bmpData[i]);
                    }
                    const base64 = btoa(binary);
                    console.log(`✅ Created BMP file (${bmpData.length} bytes)`);

                    return `data:image/bmp;base64,${base64}`;
                }

                offset += recordSize;
            }

            console.warn('No EMR_STRETCHDIBITS record found in EMF');
            return null;
        } catch (error) {
            console.error('Failed to extract bitmap from EMF:', error);
            return null;
        }
    };

    // Extract and convert images from Excel file
    const extractImagesFromExcel = async (blob: Blob): Promise<Map<string, string>> => {
        const imageMap = new Map<string, string>();
        try {
            const zip = await JSZip.loadAsync(blob);

            const mediaFolder = zip.folder("xl/media");

            if (mediaFolder) {
                const imagePromises: Promise<void>[] = [];
                mediaFolder.forEach((relativePath, file) => {
                    if (!file.dir) {
                        const promise = file.async("arraybuffer").then(async (arrayBuffer) => {
                            const ext = relativePath.split('.').pop()?.toLowerCase();
                            let dataUrl: string | null = null;

                            // Extract bitmap from EMF files
                            if (ext === 'emf') {
                                dataUrl = extractBitmapFromEmf(arrayBuffer);
                            }
                            // Handle regular image formats
                            else {
                                // Convert ArrayBuffer to base64 (browser-compatible)
                                const bytes = new Uint8Array(arrayBuffer);
                                let binary = '';
                                for (let i = 0; i < bytes.length; i++) {
                                    binary += String.fromCharCode(bytes[i]);
                                }
                                const base64 = btoa(binary);

                                let mimeType = 'image/png';
                                if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
                                else if (ext === 'gif') mimeType = 'image/gif';
                                else if (ext === 'bmp') mimeType = 'image/bmp';
                                else if (ext === 'svg') mimeType = 'image/svg+xml';
                                else if (ext === 'webp') mimeType = 'image/webp';
                                dataUrl = `data:${mimeType};base64,${base64}`;
                            }

                            if (dataUrl) {
                                imageMap.set(relativePath, dataUrl);
                                imageMap.set(relativePath.split('/').pop() || relativePath, dataUrl);
                            }
                        });
                        imagePromises.push(promise);
                    }
                });
                await Promise.all(imagePromises);
            }
        } catch (error) {
            console.error("Error extracting images from Excel:", error);
        }
        return imageMap;
    };

    useEffect(() => {
        const loadSpreadsheet = async () => {
            if (props.document && spreadsheetRef.current && !isLoaded) {
                try {
                    setLoadingBackdrop(true);
                    // Convert the blob URL to a blob
                    const response = await fetch(props.document);
                    const blob = await response.blob();

                    // Extract images from Excel
                    const imageMap = await extractImagesFromExcel(blob);

                    // Load the file into the spreadsheet
                    await spreadsheetRef.current.fromFile(blob);

                    // Fix image references using Kendo's proper structure
                    const data = await spreadsheetRef.current.saveJSON();

                    // Create root-level images object
                    const images: { [key: string]: string } = { ...data.images };
                    let imageCounter = Object.keys(images).length;
                    let hasChanges = false;

                    if (data.sheets) {
                        data.sheets.forEach((sheet: any) => {
                            if (sheet.drawings && Array.isArray(sheet.drawings)) {
                                sheet.drawings.forEach((drawing: any) => {
                                    // Fix drawings with string image paths
                                    if (drawing.image && typeof drawing.image === 'string' && drawing.image.startsWith('../')) {
                                        const filename = drawing.image.split('/').pop();
                                        const dataUrl = filename ? imageMap.get(filename) : null;

                                        if (dataUrl) {
                                            // Create unique image ID
                                            const imageId = `img_${imageCounter++}`;
                                            images[imageId] = dataUrl;
                                            drawing.image = imageId;
                                            hasChanges = true;
                                        } else {
                                            drawing._remove = true;
                                        }
                                    }
                                });

                                // Remove drawings that couldn't be fixed
                                const beforeCount = sheet.drawings.length;
                                sheet.drawings = sheet.drawings.filter((d: any) => !d._remove);
                                if (sheet.drawings.length < beforeCount) {
                                    console.log(`Removed ${beforeCount - sheet.drawings.length} unresolvable drawings`);
                                }
                            }
                        });
                    }

                    // Update images object
                    data.images = images;

                    // Reload if we made changes
                    if (hasChanges) {
                        await spreadsheetRef.current.fromJSON(data);
                    }

                    setIsLoaded(true);
                } catch (error) {
                    console.error("Error loading spreadsheet:", error);
                } finally {
                    setLoadingBackdrop(false);
                }
            }
        };

        loadSpreadsheet();
    }, [props.document, isLoaded]);

    const saveDocument = async () => {
        if (!spreadsheetRef.current) return;

        try {
            setLoadingBackdrop(true);

            // Get the spreadsheet data as JSON
            const data = spreadsheetRef.current.toJSON();

            // Fix undefined target references in drawings
            if (data.sheets) {
                data.sheets.forEach((sheet: any) => {
                    if (sheet.drawings && Array.isArray(sheet.drawings)) {
                        sheet.drawings.forEach((drawing: any) => {
                            // Ensure drawing has required properties with defaults
                            if (!drawing.topLeftCell) {
                                drawing.topLeftCell = { row: 0, col: 0, offsetX: 0, offsetY: 0 };
                            }
                            if (!drawing.bottomRightCell) {
                                drawing.bottomRightCell = { row: 1, col: 1, offsetX: 0, offsetY: 0 };
                            }
                        });
                    }
                });
            }

            // Create a Workbook from the JSON data
            const workbook = new Workbook(data);

            // Export to Excel as data URL
            const dataURL = await workbook.toDataURL();

            // Extract base64 data from data URL
            const dataSplit = dataURL.split(';base64,')[1];

            props.saveDocumentCallback({
                ...props.item,
                woid: props.woid,
                imageData: dataSplit,
                setLoadingBackdrop: setLoadingBackdrop,
                name: props.filename,
                id: props.item?.id
            });
        } catch (error) {
            console.error("Error saving spreadsheet:", error);
            setLoadingBackdrop(false);
        }
    };

    return (
        <>
            <LoadingBackdrop open={loadingBackdrop} />
            {props.save && !props.readonly && (
                <AppBar
                    position="fixed"
                    style={{
                        top: 0,
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        zIndex: 1300
                    }}
                >
                    <Toolbar style={{ justifyContent: 'flex-end' }}>
                        <Button
                            icon="save"
                            themeColor="primary"
                            onClick={saveDocument}
                            disabled={!props?.item?.allowsave}
                        >
                            Save
                        </Button>
                    </Toolbar>
                </AppBar>
            )}
            <Box style={{
                marginTop: props.save && !props.readonly ? '64px' : '0',
                width: "100%",
                height: props.save && !props.readonly ? "calc(100vh - 64px)" : "100vh"
            }}>
                <Spreadsheet
                    ref={spreadsheetRef}
                    style={{ width: "100%", height: "100%" }}
                />
            </Box>
        </>
    );
}
